import { FC, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { clearBebyggelseobjekt } from 'store/bebyggelseobjektSlice';
import { getBebyggelseobjektVersion } from 'store/bebyggelseobjektVersionSlice';
import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { RaaSpinner } from 'raa-react';

import { isOmrade } from 'components/bebyggelseobjekt/BebyggelseobjektPresentation';
import BebyggelseBeskrivningSection from 'components/bebyggelseobjekt/sections/BebyggelseBeskrivningSection';
import BebyggelseHistorikSection from 'components/bebyggelseobjekt/sections/BebyggelseHistorikSection';
import BebyggelseSkyddstypSection from 'components/bebyggelseobjekt/sections/BebyggelseSkyddstypSection';
import BebyggelseTitleSection from 'components/bebyggelseobjekt/sections/BebyggelseTitleSection';
import {
  DrawerBody,
  DrawerContainer,
  DrawerSection,
  NavigationDrawerHead
} from 'components/drawerSections/DrawerSections';
import { replaceBebyggelseobjektFeaturesInActiveSource } from 'components/map/Layers';

import { BebyggelseklassEnum, MapModeEnum } from 'enums';

import SammansattBebyggelseIngaendeObjektSection from './sections/SammansattBebyggelseIngaendeObjektSection';
import BebyggelseSkyddbestammelserSection from './sections/SammansattBebyggelseSkyddbestammelserSection';

import { getIconBySkydd, isSkyddHavt } from 'utilities/bebyggelseobjektUtilities';
import { formatDate } from 'utilities/dateUtilities';
import { getIngaendeAsPublicerade } from 'utilities/getIngaendeAsPublicerade';
import { renderWithParagraphs } from 'utilities/renderWithParagraphs';

const BebyggelseobjektHistoriskPresentation: FC = () => {
  const { id, version } = useParams();
  const dispatch = useAppDispatch();
  const { requestStatus, data: bebyggelseobjektVersion } = useAppSelector(
    (state: RootState) => state.bebyggelseobjektVersion
  );
  const domaner = useAppSelector((state: RootState) => state.bebyggelseobjektDomaner.data);

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    if (id && version && !isNaN(parseInt(version))) {
      const versionNumber = Number.parseInt(version);
      dispatch(getBebyggelseobjektVersion({ id, version: versionNumber }));
    }
    //Cleanup
    return () => {
      dispatch(clearBebyggelseobjekt());
      replaceBebyggelseobjektFeaturesInActiveSource([]);
      dispatch(setMapMode(MapModeEnum.DEFAULT));
      setDrawerMobileTemplate(undefined);
    };
  }, [dispatch, id, setDrawerMobileTemplate, version]);

  useInitialFocusOnExtent(
    useCallback(
      () =>
        bebyggelseobjektVersion &&
        replaceBebyggelseobjektFeaturesInActiveSource([
          bebyggelseobjektVersion,
          ...getIngaendeAsPublicerade(bebyggelseobjektVersion)
        ]),
      [bebyggelseobjektVersion]
    )
  );

  useEffect(() => {
    if (bebyggelseobjektVersion) {
      setDrawerMobileTemplate(
        <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
          <h2 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">{bebyggelseobjektVersion.titel}</h2>
          <div className="tw-flex tw-items-center tw-justify-start">
            <img
              className="tw-mr-2"
              src={`data:image/svg+xml;utf8,${encodeURIComponent(
                getIconBySkydd(bebyggelseobjektVersion.skydd.skyddstyp, isSkyddHavt(bebyggelseobjektVersion))
              )}`}
              alt="Ikon för bebyggelseobjekt"
            />
            <p>
              {bebyggelseobjektVersion.skydd.skyddstyp_namn}
              {bebyggelseobjektVersion.sammansatt?.havt_skydd && ' (HÄVT)'}
            </p>
          </div>
        </div>
      );
    }
  }, [setDrawerMobileTemplate, bebyggelseobjektVersion]);

  return (
    <div className="tw-h-full">
      {requestStatus === 'pending' ? (
        <div className="tw-flex tw-h-full tw-items-center tw-justify-center">
          <RaaSpinner />
        </div>
      ) : (
        <>
          {bebyggelseobjektVersion && (
            <DrawerContainer>
              <NavigationDrawerHead disableBackNavigation={false} />
              <DrawerBody>
                <div className="tw-p-4 md:tw-p-6">
                  <div className="tw-border-2 tw-border-raa-blue tw-p-3 tw-text-raa-blue">
                    <span>Äldre version publicerad {formatDate(bebyggelseobjektVersion.publiceringsdatum)}</span>
                  </div>
                </div>
                <BebyggelseTitleSection
                  skyddstyp={bebyggelseobjektVersion.skydd?.skyddstyp}
                  skyddstypNamn={bebyggelseobjektVersion.skydd?.skyddstyp_namn}
                  data={bebyggelseobjektVersion}
                  lm_andamal="(Se nuvarande version)"
                  socken="(Se nuvarande version)"
                  domaner={domaner}
                />
                <BebyggelseSkyddstypSection
                  motivering={bebyggelseobjektVersion.skydd?.motivering}
                  karaktarsdrag={bebyggelseobjektVersion.karaktarsdrag}
                  skyddstyp={bebyggelseobjektVersion.skydd?.skyddstyp}
                  lokal_klassificering={bebyggelseobjektVersion.lokal_klassificering}
                  ansvarig_myndighet="(Se nuvarande version)"
                  sammansattParentId={bebyggelseobjektVersion.sammansatt_parent_id}
                  sammansattParentNamn={bebyggelseobjektVersion.sammansatt_parent_namn}
                  isPublicerad={true}
                  skydd_subtyp={bebyggelseobjektVersion.skydd.kyrkligtkulturminneskyddstyp_namn}
                />
                {bebyggelseobjektVersion.bebyggelseklass === BebyggelseklassEnum.SAMMANSATT &&
                  bebyggelseobjektVersion.sammansatt && (
                    <>
                      <BebyggelseSkyddbestammelserSection
                        sammansattParentId={bebyggelseobjektVersion.sammansatt_parent_id}
                        skyddsbestammelser={bebyggelseobjektVersion.sammansatt?.skyddsbestammelser}
                      />
                      <SammansattBebyggelseIngaendeObjektSection
                        ingaendeObject={bebyggelseobjektVersion.sammansatt.ingaendeobjekt}
                        isPublicerad={true}
                        havtSkydd={bebyggelseobjektVersion.sammansatt.havt_skydd}
                        skyddstyp={bebyggelseobjektVersion.skydd.skyddstyp}
                      />
                    </>
                  )}
                <BebyggelseBeskrivningSection
                  domaner={domaner}
                  beskrivning={bebyggelseobjektVersion.beskrivning}
                  egenskaper={!isOmrade(bebyggelseobjektVersion) ? bebyggelseobjektVersion.egenskaper : undefined}
                />
                <BebyggelseHistorikSection
                  historik={bebyggelseobjektVersion.historik || ''}
                  handelser={bebyggelseobjektVersion.handelser}
                  domaner={domaner}
                />
                <DrawerSection>
                  <h2 className="tw-heading-md tw-mb-4">Inventeringar</h2>
                  (Se nuvarande version)
                </DrawerSection>
                <DrawerSection>
                  <h2 className="tw-heading-md tw-mb-4">Dokument</h2>
                  (Se nuvarande version)
                </DrawerSection>
                <DrawerSection>
                  <h2 className="tw-heading-md tw-mb-4">Fotografier</h2>
                  (Se nuvarande version)
                </DrawerSection>
                <DrawerSection>
                  <h2 className="tw-heading-md tw-mb-4">Referenser</h2>
                  {renderWithParagraphs(bebyggelseobjektVersion.referenser || '–')}
                </DrawerSection>
              </DrawerBody>
            </DrawerContainer>
          )}
        </>
      )}
    </div>
  );
};

export default BebyggelseobjektHistoriskPresentation;
